import { Dialog } from '@base-ui/react/dialog'
import { type ReactNode, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { overlayCopy } from '@/copy'
import { cx } from '../cx'
import { Icon } from '../Icon/Icon'
import { IconButton } from '../IconButton/IconButton'
import { useOverlayLayer } from '../overlay/escapeStack'
import { OverlayFooter, OverlayHeader, overlayClass } from '../overlay/Overlay'
import styles from './Drawer.module.css'

// Wraps Base UI Dialog as an edge panel (Drawer card). Deviations: Escape via
// the page-wide stack; closes on route change. When the pathname changes the
// frame moves focus to the new page's h1, so the drawer leaves focus alone; a
// same-path navigation (query or hash only) returns it to the trigger. Tested.

export function Drawer({
  open,
  onClose,
  title,
  side = 'inline-start',
  size = 'default',
  footer,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  side?: 'inline-start' | 'inline-end' | 'block-end'
  size?: 'default' | 'full'
  footer?: ReactNode
  children: ReactNode
}) {
  const handle = useOverlayLayer(open, (next) => {
    if (!next) onClose()
  })

  // The drawer closes on navigation, in an effect and not during render.
  const { key, pathname } = useLocation()
  const previousKey = useRef(key)
  const previousPath = useRef(pathname)
  const openRef = useRef(open)
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    openRef.current = open
    onCloseRef.current = onClose
  })
  const leaveFocusToPage = useRef(false)
  useEffect(() => {
    if (open) leaveFocusToPage.current = false
  }, [open])
  useEffect(() => {
    if (previousKey.current === key) return
    previousKey.current = key
    const pathChanged = previousPath.current !== pathname
    previousPath.current = pathname
    if (!openRef.current) return
    leaveFocusToPage.current = pathChanged
    onCloseRef.current()
  }, [key, pathname])

  return (
    <Dialog.Root open={open} onOpenChange={handle}>
      <Dialog.Portal>
        <Dialog.Backdrop className={overlayClass.scrim} data-layer="drawer" />
        <Dialog.Viewport className={styles.viewport}>
          <Dialog.Popup
            className={cx(overlayClass.surface, styles.popup)}
            aria-modal="true"
            data-weight="lg"
            data-side={side}
            data-size={size}
            finalFocus={() => !leaveFocusToPage.current}
          >
            <OverlayHeader
              title={
                <Dialog.Title className={overlayClass.title}>
                  {title}
                </Dialog.Title>
              }
              close={
                <Dialog.Close
                  render={
                    <IconButton label={overlayCopy.close}>
                      <Icon name="cross-2" />
                    </IconButton>
                  }
                />
              }
            />
            <div className={styles.body}>{children}</div>
            {footer && <OverlayFooter>{footer}</OverlayFooter>}
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
